import React, { useState, useEffect, useCallback } from "react";
import { customFetchV2 } from "../../../services/custom-fetch-v2.ts";
import Loading from "../Loading.jsx";
import SmartLink from "../SmartLink.jsx";
import SectionHeader from "./SectionHeader.tsx";
import Button from "./Button.tsx";
import { useLocation } from 'react-router-dom';
import openExternalPage from '../../../services/openExternalPage';


interface PaymentMethod {
  id: string;
  description: string;
  isDefault: boolean;
}

interface ApiResponse<T> {
  ok: boolean;
  data: T;
}

interface UpdatePaymentMethodResponse {
  success: boolean;
}

interface RedirectUrlResponse {
  redirectUrl: string;
}

const BillingPaymentMethods: React.FC = () => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [checkoutSessionIsLoading, setCheckoutSessionIsLoading] =
    useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const location = useLocation();
  
  // Typically triggered when the user returns from the payment method update
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);

    //const action = searchParams.get("action");
    const success = searchParams.get("success");

    if (success === 'true') {
      console.log('Reloading payment methods after successful action');
      loadPaymentMethods();
    }

    setCheckoutSessionIsLoading(false);
  }, [location.search]);

  const loadPaymentMethods = useCallback((skipLoading: boolean = false) => {
    if (!skipLoading) {
      setIsLoading(true);
    }

    return customFetchV2<PaymentMethod[]>("/api/billing/payment-methods", {
      method: "GET",
    })
      .then((response: ApiResponse<PaymentMethod[]>) => {
        if (!response.ok) {
          throw new Error("Ooops");
        }
        return response.data;
      })
      .then((data: PaymentMethod[]) => {
        setIsLoading(false);
        setPaymentMethods(data);
      })
      .catch((error: Error) => {
        setErrorMsg(error.message);
        setIsLoading(false);
      });
  }, []);

  // Load payment methods when the component mounts
  useEffect(() => {
    loadPaymentMethods();
  }, [loadPaymentMethods]);

  const onSetPrimaryCard = (method: PaymentMethod) => () => {
    return customFetchV2<UpdatePaymentMethodResponse>(
      `/api/billing/payment-methods/${method.id}`,
      {
        method: "PUT",
        body: JSON.stringify({
          isDefault: true,
        }),
      }
    )
      .then((response: ApiResponse<UpdatePaymentMethodResponse>) => {
        if (!response.ok) {
          throw new Error("Unable to set default.");
        }
        return response.data;
      })
      .then((data: UpdatePaymentMethodResponse) => {
        if (!data.success) {
          throw new Error("Unable to set default.");
        }
        return loadPaymentMethods(true);
      })
      .catch((error: Error) => {
        setErrorMsg(error.message);
        setIsLoading(false);
      });
  };

  const createUpdatePaymentMethodSession = () => {
    setCheckoutSessionIsLoading(true);

    const requestPath = `/api/billing/update-payment-method-sessions?platform=${process.env.PLATFORM}`;

    return customFetchV2<RedirectUrlResponse>(requestPath, {
      method: "POST",
    })
      .then((response: ApiResponse<RedirectUrlResponse>) => {
        if (!response.ok) {
          throw new Error("Ooops");
        }
        return response.data;
      })
      .then((data: RedirectUrlResponse) => {
        openExternalPage(data.redirectUrl);
      })
      .catch((_error: Error) => {
        setCheckoutSessionIsLoading(false);
      });
  };

  const renderPaymentMethods = (methods: PaymentMethod[]) => {
    return methods.map((method, ix) => (
      <PaymentMethodRow
        key={ix}
        method={method}
        onSetPrimary={onSetPrimaryCard(method)}
      />
    ));
  };

  return (
    <div className="section">
      <SectionHeader
        title="Payment Methods"
        rightContent={
          <Button
            label="+"
            onClick={createUpdatePaymentMethodSession}
            loading={checkoutSessionIsLoading}
          />
        }
      />
      {isLoading ? (
        <Loading />
      ) : (
        <table className="table is-fullwidth">
          <tbody>{renderPaymentMethods(paymentMethods)}</tbody>
        </table>
      )}
    </div>
  );
};

interface PaymentMethodRowProps {
  method: PaymentMethod;
  onSetPrimary: () => void;
}

const PaymentMethodRow: React.FC<PaymentMethodRowProps> = ({
  method,
  onSetPrimary,
}) => {
  const { description, isDefault } = method;

  return (
    <tr>
      <td></td>
      <td>{description}</td>
      <td>
        {isDefault ? (
          <>Primary</>
        ) : (
          <>
            <SmartLink onClick={onSetPrimary} />
          </>
        )}
      </td>
    </tr>
  );
};

// Add this to make TypeScript recognize the cordova property on the window object
declare global {
  interface Window {
    cordova?: {
      InAppBrowser: {
        open: (url: string, target: string) => void;
      };
    };
  }
}

export default BillingPaymentMethods;
